import { useState } from "react";
import { useForm } from "react-hook-form";
import { FcGoogle } from "react-icons/fc";
import {
  HiEye,
  HiEyeOff,
  HiLocationMarker,
  HiLockClosed,
  HiMail,
  HiPhone,
  HiUser,
  HiUserCircle,
} from "react-icons/hi";
import { Link } from "react-router-dom";

import { RegisterCredentials } from "@/types/auth";

interface DriverFormData extends RegisterCredentials {
  age?: number;
  availability?: string;
  city: string;
  deliveryType?: string;
  hasValidLicense: boolean;
  hasVehicleAccess?: boolean;
  phoneNumber: string;
  scooterExperience?: string;
}

interface DriverRegisterFormProps {
  error?: string;
  isLoading?: boolean;
  onGoogleLogin: () => void;
  onSubmit: (data: RegisterCredentials) => void;
  vehicleType: "delivery" | "scooter" | "taxi";
}

const vehicleTypeConfig = {
  delivery: {
    color: "text-pink-400",
    earnings: "R3,500",
    features: [
      {
        subtitle: "Competitive rates with food, groceries, and packages!",
        title: "Earn R3,500+/week",
      },
      {
        subtitle: "Choose your ideal working schedule",
        title: "Flexible Hours",
      },
      {
        subtitle: "Comprehensive coverage for deliveries",
        title: "Insurance Coverage",
      },
      { subtitle: "24/7 driver support and assistance", title: "Support Team" },
    ],
    title: "Become a delivery driver",
  },
  scooter: {
    color: "text-cyan-400",
    earnings: "R2,800",
    features: [
      {
        subtitle: "Competitive rates with eco-friendly bonuses",
        title: "Earn R2,800+/week",
      },
      {
        subtitle: "Navigate traffic quickly and work your hours",
        title: "Quick & Flexible",
      },
      {
        subtitle: "Comprehensive safety training included",
        title: "Safety First",
      },
      { subtitle: "Help reduce carbon emissions", title: "Green Transport" },
    ],
    title: "Become a scooter driver",
  },
  taxi: {
    color: "text-blue-400",
    earnings: "R6,500",
    features: [
      {
        subtitle: "Receive earnings potential with weekly payouts",
        title: "Earn R4,500+/week",
      },
      {
        subtitle: "Drive when you want, where you want",
        title: "Flexible Schedule",
      },
      {
        subtitle: "Comprehensive coverage while driving",
        title: "Insurance Coverage",
      },
      { subtitle: "Round the clock driver assistance", title: "24/7 Support" },
    ],
    title: "Become Taxi driver",
  },
};

export default function DriverRegisterForm({
  error,
  isLoading = false,
  onGoogleLogin,
  onSubmit,
  vehicleType,
}: DriverRegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const config = vehicleTypeConfig[vehicleType];

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<DriverFormData>();

  const password = watch("password");

  const handleFormSubmit = (data: DriverFormData) => {
    const registrationData: RegisterCredentials = {
      acceptTerms: data.acceptTerms,
      city: data.city,
      confirmPassword: data.confirmPassword,
      email: data.email,
      firstName: data.firstName,
      isDriverRegistration: true,
      lastName: data.lastName,
      password: data.password,
      phoneNumber: data.phoneNumber,
      vehicleType,
    };
    onSubmit(registrationData);
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-base-200 to-base-300">
      {/* Left side - Marketing content */}
      <div className="hidden flex-col justify-center bg-gradient-to-br from-primary to-secondary p-12 text-white lg:flex lg:w-1/2">
        <div className="mb-8">
          <div className="mb-6 flex items-center">
            <div className="mr-3 rounded-lg bg-white/20 p-2">
              <HiUserCircle className="h-6 w-6" />
            </div>
            <span className="text-lg font-medium">Join 5,000+ Eco Drivers</span>
          </div>

          <h1 className="mb-4 text-5xl font-bold">
            Earn up to <span className={config.color}>{config.earnings}</span>
          </h1>
          <h2 className="mb-6 text-3xl font-bold">
            per week {vehicleType === "scooter" ? "with" : "by"}{" "}
            {vehicleType === "scooter" && (
              <span className="text-purple-300">electric scooters</span>
            )}
            {vehicleType === "taxi" && (
              <span className="text-blue-300">driving</span>
            )}
            {vehicleType === "delivery" && (
              <span className="text-pink-300">delivery</span>
            )}
            {vehicleType !== "scooter" && " with Hareeng"}
          </h2>

          {vehicleType === "scooter" && (
            <p className="mb-8 text-xl text-gray-200">
              Join the eco-friendly revolution! Become a Hareeng scooter driver
              and earn while helping the environment.
            </p>
          )}
          {vehicleType === "taxi" && (
            <p className="mb-8 text-xl text-gray-200">
              Become a Hareeng driver, set your schedule and earn money by
              driving!
            </p>
          )}
          {vehicleType === "delivery" && (
            <p className="mb-8 text-xl text-gray-200">
              Become a Hareeng delivery partner and earn money delivering food,
              groceries, and packages!
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-6">
          {config.features.map((feature, index) => (
            <div className="flex items-start space-x-3" key={index}>
              <div className="rounded-lg bg-white/20 p-2">
                {index === 0 && (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-400 text-sm font-bold text-white">
                    ₹
                  </div>
                )}
                {index === 1 && (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-400 text-sm text-white">
                    ⏰
                  </div>
                )}
                {index === 2 && (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-pink-400 text-sm text-white">
                    🛡️
                  </div>
                )}
                {index === 3 && (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-cyan-400 text-sm text-white">
                    💬
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold">{feature.title}</h3>
                <p className="text-sm text-gray-200">{feature.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right side - Registration form */}
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="card w-full max-w-md bg-base-100 shadow-xl">
          <div className="card-body">
            {/* Header */}
            <div className="mb-6 text-center">
              <h2 className="text-2xl font-bold">{config.title}</h2>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="alert alert-error mb-4">
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form
              className="space-y-4"
              onSubmit={handleSubmit(handleFormSubmit)}
            >
              {/* Name fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">First Name</span>
                  </label>
                  <div className="relative">
                    <HiUser className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <input
                      className={`input input-bordered w-full pl-10 ${
                        errors.firstName ? "input-error" : ""
                      }`}
                      placeholder="Enter first name"
                      type="text"
                      {...register("firstName", {
                        required: "First name is required",
                      })}
                    />
                  </div>
                  {errors.firstName && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.firstName.message}
                      </span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Last Name</span>
                  </label>
                  <div className="relative">
                    <HiUser className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <input
                      className={`input input-bordered w-full pl-10 ${
                        errors.lastName ? "input-error" : ""
                      }`}
                      placeholder="Enter last name"
                      type="text"
                      {...register("lastName", {
                        required: "Last name is required",
                      })}
                    />
                  </div>
                  {errors.lastName && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.lastName.message}
                      </span>
                    </label>
                  )}
                </div>
              </div>

              {/* Age field for scooter drivers */}
              {vehicleType === "scooter" && (
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Age</span>
                  </label>
                  <input
                    className={`input input-bordered w-full ${
                      errors.age ? "input-error" : ""
                    }`}
                    placeholder="Your age"
                    type="number"
                    {...register("age", {
                      min: {
                        message: "Must be at least 18 years old",
                        value: 18,
                      },
                      required: "Age is required",
                    })}
                  />
                  <label className="label">
                    <span className="label-text-alt">
                      Must be at least 18 years old
                    </span>
                  </label>
                  {errors.age && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.age.message}
                      </span>
                    </label>
                  )}
                </div>
              )}

              {/* Email */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Email</span>
                </label>
                <div className="relative">
                  <HiMail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    className={`input input-bordered w-full pl-10 ${
                      errors.email ? "input-error" : ""
                    }`}
                    placeholder="Enter email address"
                    type="email"
                    {...register("email", {
                      pattern: {
                        message: "Email is invalid",
                        value: /\S+@\S+\.\S+/,
                      },
                      required: "Email is required",
                    })}
                  />
                </div>
                {errors.email && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.email.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Phone Number */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Phone number</span>
                </label>
                <div className="flex">
                  <select className="select select-bordered w-20">
                    <option>+27</option>
                  </select>
                  <div className="relative ml-2 flex-1">
                    <HiPhone className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <input
                      className={`input input-bordered w-full pl-10 ${
                        errors.phoneNumber ? "input-error" : ""
                      }`}
                      placeholder="Mobile number"
                      type="tel"
                      {...register("phoneNumber", {
                        required: "Phone number is required",
                      })}
                    />
                  </div>
                </div>
                {errors.phoneNumber && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.phoneNumber.message}
                    </span>
                  </label>
                )}
              </div>

              {/* City */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">City</span>
                </label>
                <div className="relative">
                  <HiLocationMarker className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <select
                    className={`select select-bordered w-full pl-10 ${
                      errors.city ? "select-error" : ""
                    }`}
                    {...register("city", {
                      required: "City is required",
                    })}
                  >
                    <option value="">Select your city</option>
                    <option value="johannesburg">Johannesburg</option>
                    <option value="cape-town">Cape Town</option>
                    <option value="durban">Durban</option>
                    <option value="pretoria">Pretoria</option>
                    <option value="port-elizabeth">Port Elizabeth</option>
                    <option value="bloemfontein">Bloemfontein</option>
                  </select>
                </div>
                {errors.city && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.city.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Vehicle type specific fields */}
              {vehicleType === "scooter" && (
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Scooter Experience</span>
                  </label>
                  <select
                    className="select select-bordered w-full"
                    {...register("scooterExperience")}
                  >
                    <option value="">Select experience level</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="experienced">Experienced</option>
                  </select>
                </div>
              )}

              {vehicleType === "delivery" && (
                <>
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">
                        Preferred Delivery Type
                      </span>
                    </label>
                    <select
                      className="select select-bordered w-full"
                      {...register("deliveryType")}
                    >
                      <option value="">Select delivery type</option>
                      <option value="food">Food delivery</option>
                      <option value="groceries">Groceries</option>
                      <option value="packages">Packages</option>
                      <option value="all">All types</option>
                    </select>
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Vehicle Type</span>
                    </label>
                    <select className="select select-bordered w-full">
                      <option value="">Select vehicle type</option>
                      <option value="bicycle">Bicycle</option>
                      <option value="motorbike">Motorbike</option>
                      <option value="car">Car</option>
                      <option value="van">Van</option>
                    </select>
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Availability</span>
                    </label>
                    <select
                      className="select select-bordered w-full"
                      {...register("availability")}
                    >
                      <option value="">Select availability</option>
                      <option value="part-time">Part-time</option>
                      <option value="full-time">Full-time</option>
                      <option value="weekends">Weekends only</option>
                      <option value="evenings">Evenings</option>
                    </select>
                  </div>
                </>
              )}

              {/* Password */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Password</span>
                </label>
                <div className="relative">
                  <HiLockClosed className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    className={`input input-bordered w-full pl-10 pr-10 ${
                      errors.password ? "input-error" : ""
                    }`}
                    placeholder="Create password"
                    type={showPassword ? "text" : "password"}
                    {...register("password", {
                      minLength: {
                        message: "Password must be at least 8 characters",
                        value: 8,
                      },
                      required: "Password is required",
                    })}
                  />
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                    type="button"
                  >
                    {showPassword ? (
                      <HiEyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <HiEye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.password.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Confirm Password */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Confirm Password</span>
                </label>
                <div className="relative">
                  <HiLockClosed className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    className={`input input-bordered w-full pl-10 pr-10 ${
                      errors.confirmPassword ? "input-error" : ""
                    }`}
                    placeholder="Confirm password"
                    type={showConfirmPassword ? "text" : "password"}
                    {...register("confirmPassword", {
                      required: "Please confirm your password",
                      validate: (value) =>
                        value === password || "Passwords do not match",
                    })}
                  />
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    type="button"
                  >
                    {showConfirmPassword ? (
                      <HiEyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <HiEye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.confirmPassword.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Checkboxes */}
              <div className="space-y-2">
                {vehicleType === "scooter" && (
                  <>
                    <div className="form-control">
                      <label className="label cursor-pointer justify-start">
                        <input
                          className="checkbox-primary checkbox mr-3"
                          type="checkbox"
                          {...register("hasValidLicense")}
                        />
                        <span className="label-text">
                          I own or have access to an electric scooter
                        </span>
                      </label>
                    </div>
                    <div className="form-control">
                      <label className="label cursor-pointer justify-start">
                        <input
                          className="checkbox-primary checkbox mr-3"
                          type="checkbox"
                        />
                        <span className="label-text">
                          I have a certified safety helmet
                        </span>
                      </label>
                    </div>
                    <div className="form-control">
                      <label className="label cursor-pointer justify-start">
                        <input
                          className="checkbox-primary checkbox mr-3"
                          type="checkbox"
                        />
                        <span className="label-text">
                          I have a valid drivers license (optional for scooters)
                        </span>
                      </label>
                    </div>
                  </>
                )}

                {vehicleType === "taxi" && (
                  <div className="form-control">
                    <label className="label cursor-pointer justify-start">
                      <input
                        className="checkbox-primary checkbox mr-3"
                        type="checkbox"
                        {...register("hasValidLicense", {
                          required: "Valid driver's license is required",
                        })}
                      />
                      <span className="label-text">
                        I have a valid drivers license and PDP
                      </span>
                    </label>
                    {errors.hasValidLicense && (
                      <span className="mt-1 text-sm text-error">
                        {errors.hasValidLicense.message}
                      </span>
                    )}
                  </div>
                )}

                {vehicleType === "delivery" && (
                  <>
                    <div className="form-control">
                      <label className="label cursor-pointer justify-start">
                        <input
                          className="checkbox-primary checkbox mr-3"
                          type="checkbox"
                          {...register("hasVehicleAccess")}
                        />
                        <span className="label-text">
                          I own or have access to a delivery vehicle
                        </span>
                      </label>
                    </div>
                    <div className="form-control">
                      <label className="label cursor-pointer justify-start">
                        <input
                          className="checkbox-primary checkbox mr-3"
                          type="checkbox"
                          {...register("hasValidLicense")}
                        />
                        <span className="label-text">
                          I have a valid drivers license or applicable
                        </span>
                      </label>
                    </div>
                  </>
                )}

                {/* Terms acceptance */}
                <div className="form-control">
                  <label className="label cursor-pointer justify-start">
                    <input
                      className="checkbox-primary checkbox mr-3"
                      type="checkbox"
                      {...register("acceptTerms", {
                        required: "You must accept the terms of service",
                      })}
                    />
                    <span className="label-text text-sm">
                      By registering, you agree to our{" "}
                      <Link className="link link-primary" to="/legal/terms">
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link className="link link-primary" to="/legal/privacy">
                        Privacy Policy
                      </Link>
                    </span>
                  </label>
                  {errors.acceptTerms && (
                    <span className="mt-1 text-sm text-error">
                      {errors.acceptTerms.message}
                    </span>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <button
                className={`btn btn-primary w-full ${isLoading ? "loading" : ""}`}
                disabled={isLoading}
                type="submit"
              >
                {isLoading
                  ? "Registering..."
                  : `Register as ${vehicleType} driver`}
              </button>

              {/* Divider */}
              <div className="divider">OR</div>

              {/* Google Login
              <button
                className="btn btn-outline w-full"
                disabled={isLoading}
                onClick={onGoogleLogin}
                type="button"
              >
                <FcGoogle className="mr-2 h-5 w-5" />
                Continue with Google
              </button> */}

              {/* Login Link */}
              <div className="text-center text-sm">
                <span className="text-gray-500">Already have an account? </span>
                <Link
                  className="link link-primary font-medium"
                  to="/auth/login"
                >
                  Log in
                </Link>
              </div>

              {/* Fleet Owner Link */}
              {vehicleType === "delivery" && (
                <div className="text-center text-sm">
                  <span className="text-gray-500">
                    If you have multiple vehicles and drivers,{" "}
                  </span>
                  <Link
                    className="link link-primary font-medium"
                    to="/auth/register?type=fleet"
                  >
                    register as a Fleet Owner →
                  </Link>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
